/* iter-50d — Phase C: Stacks title selection dialog.
 *
 * Opens when a card in StacksPage is clicked. Routes the author/admin
 * through four branching actions per the user spec:
 *   1. Open / View — navigate to The Loom (project) or the bookstore page (book)
 *   2. Send to Bookstore — for project source, navigates to the Publish flow
 *   3. Cover art — upload a cover image (8 MB max, image/* MIME)
 *   4. Cover copy — edit the back-of-book / cover blurb
 *
 * Phase D (signed-cover Stripe + author signature email) hangs off the
 * "Cover art" step's "Order signed cover" sub-action — stubbed today.
 */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, PenLine, BookOpen, ImagePlus, Type, Send,
  Loader2, Check, FileUp, UserPlus, ShieldAlert,
} from 'lucide-react';
import { projectsApi } from '@/lib/api';
import { bookstoreAdminApi } from '@/lib/bookstoreApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = {
  ROUTER:      'router',
  UPLOAD_COVER:'upload_cover',
  EDIT_COPY:   'edit_copy',
  REASSIGN:    'reassign',          // iter-50f — admin-only
};

export default function StacksTitleDialog({ open, row, isAdmin = false, onClose, onChanged }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState(STEPS.ROUTER);
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState('');
  const fileRef = useRef(null);

  // iter-50f — Reassign-book-to-author state.
  const [reEmail,    setReEmail]    = useState('');
  const [reProjects, setReProjects] = useState(null); // null=no pick yet, []=loaded list
  const [rePid,      setRePid]      = useState('');

  if (!row) return null;
  const isProject = row.source === 'project';

  const close = () => {
    setStep(STEPS.ROUTER); setCopy(''); setBusy(false);
    setReEmail(''); setReProjects(null); setRePid('');
    onClose?.();
  };

  // --- Action handlers ---
  const openInWorkspace = () => {
    if (isProject) nav(`/app?project=${encodeURIComponent(row.project_id)}`);
    else nav(`/bookstore`);                             // bookstore landing (the book is listed)
    close();
  };

  const sendToBookstore = () => {
    if (!isProject) return;
    // Navigate to the workspace with a flag the AppShell uses to auto-open
    // the existing PublishToBookstoreDialog.
    nav(`/app?project=${encodeURIComponent(row.project_id)}&action=publish`);
    close();
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('stacks.pickImageFirst')); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const url = isProject
        ? `${API}/projects/${row.project_id}/cover-image`
        : `${API}/admin/bookstore/books/${row.book_id}/cover-image`;
      const { data } = await axios.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true, timeout: 60000,
      });
      toast.success(t('stacks.coverSaved'));
      onChanged?.({ ...row, cover_url: data.cover_url });
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || t('stacks.uploadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const saveCopy = async () => {
    if (!copy.trim()) { toast.error(t('stacks.copyEmpty')); return; }
    setBusy(true);
    try {
      if (isProject) {
        await projectsApi.update(row.project_id, { cover_copy: copy });
      } else {
        await bookstoreAdminApi.updateBook(row.book_id, { back_copy: copy });
      }
      toast.success(t('stacks.copySaved'));
      onChanged?.(row);
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || t('stacks.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  // iter-50f — Admin reassign book to author. Two-call sequence when the
  // author owns >1 project (the backend returns 409 with the project list
  // for the admin to pick from).
  const submitReassign = async () => {
    if (!reEmail.trim()) { toast.error(t('stacks.enterAuthorEmail')); return; }
    setBusy(true);
    try {
      const body = { author_email: reEmail.trim() };
      if (rePid) body.project_id = rePid;
      await bookstoreAdminApi.reassignBook(row.book_id, body);
      toast.success(t('stacks.reassignSuccess', { title: row.title, email: reEmail.trim() }));
      onChanged?.(row);
      close();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // 409 from backend carries the project list to choose from.
      if (err?.response?.status === 409 && typeof detail === 'object' && Array.isArray(detail.projects)) {
        setReProjects(detail.projects);
        toast.info(t('stacks.reassignMultiple'));
      } else {
        toast.error(typeof detail === 'string' ? detail : (err.message || t('stacks.reassignFailed')));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md" data-testid="stacks-title-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            {isProject
              ? <PenLine className="h-4 w-4 text-primary" />
              : <BookOpen className="h-4 w-4 text-primary" />}
            {row.title}
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            by {row.author || '—'} · {isProject ? t('stacks.loomDraft') : t('stacks.bookstoreTitle')}
          </DialogDescription>
        </DialogHeader>

        {step === STEPS.ROUTER && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="stacks-dialog-router">
            <ActionCard
              icon={isProject ? <PenLine className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
              title={isProject ? t('stacks.openInLoom') : t('stacks.viewOnBookstore')}
              desc={isProject ? t('stacks.assignToProject') : t('stacks.seePublicListing')}
              testid="stacks-action-open"
              onClick={openInWorkspace}
            />
            {isProject && (
              <ActionCard
                icon={<Send className="h-4 w-4" />}
                title={t('stacks.sendToBookstore')}
                desc={row.can_promote ? t('stacks.publishDraftHint') : t('stacks.alreadyPublished')}
                testid="stacks-action-publish"
                onClick={row.can_promote ? sendToBookstore : openInWorkspace}
                disabled={!row.can_promote}
              />
            )}
            <ActionCard
              icon={<ImagePlus className="h-4 w-4" />}
              title={t('stacks.coverArt')}
              desc={row.cover_url ? t('stacks.replaceCover') : t('stacks.uploadCoverHint')}
              testid="stacks-action-cover"
              onClick={() => setStep(STEPS.UPLOAD_COVER)}
              hidden={!isProject && !isAdmin}
            />
            <ActionCard
              icon={<Type className="h-4 w-4" />}
              title={t('stacks.coverCopy')}
              desc={t('stacks.editBlurb')}
              testid="stacks-action-copy"
              onClick={() => setStep(STEPS.EDIT_COPY)}
              hidden={!isProject && !isAdmin}
            />
            {/* iter-50f — admin-only reassign for bookstore rows. Lets an admin
                attach an orphan/imported book to a The Loom author so it
                appears in that author's /stacks view. */}
            {!isProject && isAdmin && (
              <ActionCard
                icon={<UserPlus className="h-4 w-4" />}
                title={t('stacks.reassignToAuthor')}
                desc={t('stacks.reassignHint')}
                testid="stacks-action-reassign"
                onClick={() => setStep(STEPS.REASSIGN)}
              />
            )}
          </div>
        )}

        {step === STEPS.REASSIGN && (
          <div className="space-y-3" data-testid="stacks-dialog-reassign">
            <div className="flex items-center gap-2 rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800">
              <ShieldAlert className="h-3.5 w-3.5" />
              {t('stacks.adminOnlyReassign')}
            </div>
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('stacks.authorEmail')}
              </span>
              <input
                type="email"
                autoFocus
                value={reEmail}
                onChange={(e) => { setReEmail(e.target.value); setReProjects(null); setRePid(''); }}
                placeholder="author@example.com"
                data-testid="stacks-reassign-email"
                className="h-9 w-full rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary"
              />
            </label>

            {Array.isArray(reProjects) && reProjects.length > 0 && (
              <label className="block">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('stacks.multipleProjects', { count: reProjects.length })}
                </span>
                <select
                  value={rePid}
                  onChange={(e) => setRePid(e.target.value)}
                  data-testid="stacks-reassign-project"
                  className="h-9 w-full rounded-md border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary"
                >
                  <option value="">{t('stacks.selectProjectEllipsis')}</option>
                  {reProjects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name || p.id}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(STEPS.ROUTER)} data-testid="stacks-reassign-back">
                <ArrowLeft className="mr-1 h-3 w-3" /> {t('stacks.back')}
              </Button>
              <Button
                onClick={submitReassign}
                disabled={busy || !reEmail.trim() || (Array.isArray(reProjects) && reProjects.length > 0 && !rePid)}
                data-testid="stacks-reassign-submit"
              >
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
                {t('stacks.reassign')}
              </Button>
            </div>
          </div>
        )}

        {step === STEPS.UPLOAD_COVER && (
          <div className="space-y-3" data-testid="stacks-dialog-upload">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-background p-8 text-center hover:border-primary/60"
              data-testid="stacks-cover-dropzone"
            >
              <FileUp className="h-6 w-6 text-muted-foreground" />
              <div className="text-[12.5px] font-medium">
                {busy ? t('stacks.uploading') : t('stacks.clickToPickImage')}
              </div>
              <div className="text-[10.5px] text-muted-foreground">
                {t('stacks.coverRequirements')}
              </div>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              data-testid="stacks-cover-fileinput"
              onChange={(e) => uploadCover(e.target.files?.[0])}
            />
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(STEPS.ROUTER)} data-testid="stacks-dialog-back">
                <ArrowLeft className="mr-1 h-3 w-3" /> {t('stacks.back')}
              </Button>
            </div>
          </div>
        )}

        {step === STEPS.EDIT_COPY && (
          <div className="space-y-3" data-testid="stacks-dialog-copy">
            <Textarea
              value={copy}
              onChange={(e) => setCopy(e.target.value)}
              rows={8}
              placeholder={t('stacks.backCoverPlaceholder')}
              maxLength={1500}
              spellCheck
              data-testid="stacks-copy-textarea"
              className="resize-none border-border bg-background font-serif text-[13px] leading-relaxed"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] text-muted-foreground">{copy.length}/1500</span>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setStep(STEPS.ROUTER)} data-testid="stacks-copy-back">
                  <ArrowLeft className="mr-1 h-3 w-3" /> {t('stacks.back')}
                </Button>
                <Button onClick={saveCopy} disabled={busy || !copy.trim()} data-testid="stacks-copy-save">
                  {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
                  {t('stacks.saveCopy')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ActionCard({ icon, title, desc, testid, onClick, disabled, hidden }) {
  if (hidden) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="group flex flex-col items-start gap-1 rounded-md border border-border bg-card p-3 text-left transition hover:border-primary/60 hover:bg-secondary/30 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="flex items-center gap-2 text-[12.5px] font-semibold text-primary">
        {icon}{title}
      </span>
      <span className="text-[11px] leading-snug text-muted-foreground">{desc}</span>
    </button>
  );
}
